import { AlertTriangle, Download, FileText, RotateCw } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import LoadingButton from "@components/ui-components/loading-button";
import { ComponentSpinner } from "@components/spinner";
import {
    PREVIEW_KIND,
    VIEWER_ERROR,
    VIEWER_STATUS
} from "../misc/constants";

const FRAME_CLASS = "h-[60vh] w-full rounded-lg border border-gray-200 bg-light/60";

// The one route to reading a contract the browser cannot embed. The download
// is tracked because acknowledgement is gated on the document having been
// opened one way or the other.
const Fallback = ({ icon: Icon, title, message, url, onOpen, onRetry }) => (
    <div className={classnames(FRAME_CLASS, "flex flex-col items-center justify-center gap-3 p-6 text-center")}>
        <span className="grid h-12 w-12 place-items-center rounded-lg border border-gray-200 bg-white text-primary">
            <Icon className="h-6 w-6" />
        </span>
        <div>
            <p className="text-sm font-medium text-gray-900">{title}</p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">{message}</p>
        </div>
        <div className="flex items-center gap-2">
            {onRetry && (
                <LoadingButton
                    color="neutral"
                    size="sm"
                    icon={RotateCw}
                    text="Try again"
                    onClick={onRetry}
                    testId="retry-contract-preview" />
            )}
            {url && (
                <LoadingButton
                    color="dark"
                    size="sm"
                    icon={Download}
                    text="Open contract"
                    href={url}
                    target="_blank"
                    onClick={onOpen}
                    testId="open-contract-preview" />
            )}
        </div>
    </div>
);

Fallback.propTypes = {
    icon   : PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    title  : PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    url    : PropTypes.string,
    onOpen : PropTypes.func,
    onRetry: PropTypes.func
};

// The browser's own PDF reader, which is how every contract is displayed. The
// document is the blob the /document route served, framed from a same-origin
// object URL — acknowledgement is gated on the frame having actually loaded.
//
// `sandbox` is deliberately omitted: Chrome and Firefox run their PDF viewer as
// a plugin document, which a sandboxed frame is not allowed to instantiate.
//
// There is no "open in new tab" affordance here if it came up blank: the
// caller's download action below points at the same URL, so a second button
// doing the same thing would only cost the page a row of height.
const ContractFrame = ({ contract, viewer }) => (
    <div className="relative">
        <iframe
            key={viewer.frameKey}
            src={contract.url}
            title={`${contract.name}, contract document`}
            onLoad={viewer.onFrameReady}
            referrerPolicy="no-referrer"
            className={classnames(FRAME_CLASS, "block")}
            data-cy="native-contract-preview" />

        {!viewer.frameReady && (
            <div className="absolute inset-0 grid place-items-center rounded-lg bg-light/60">
                <ComponentSpinner />
            </div>
        )}
    </div>
);

ContractFrame.propTypes = {
    contract: PropTypes.object.isRequired,
    viewer  : PropTypes.object.isRequired
};

// The contract document itself, in the browser's own reader, with the download
// as the last resort for anything it will not display.
//
// Word files are deliberately not routed through a third-party web viewer,
// since that would mean handing a signed agreement to an external service.
const DocumentPreview = ({ contract, viewer, onOpenExternally }) => {
    if (viewer.loading) {
        return (
            <div className={classnames(FRAME_CLASS, "grid place-items-center")}>
                <ComponentSpinner />
            </div>
        );
    }

    if (contract.previewKind === PREVIEW_KIND.NONE) {
        return (
            <Fallback
                icon={AlertTriangle}
                title="No readable file attached"
                message="This contract has no document stored against it. Ask ops to upload it again before you acknowledge anything." />
        );
    }

    if (contract.previewKind === PREVIEW_KIND.DOC) {
        return (
            <Fallback
                icon={FileText}
                title={contract.name}
                message="Word documents cannot be displayed in the browser. Open the file to read it in full, then confirm below."
                url={contract.url}
                onOpen={onOpenExternally} />
        );
    }

    if (viewer.status === VIEWER_STATUS.ERROR) {
        return (
            <Fallback
                icon={AlertTriangle}
                title={VIEWER_ERROR.title}
                message={viewer.error || VIEWER_ERROR.message}
                url={contract.url}
                onOpen={onOpenExternally}
                onRetry={viewer.retry} />
        );
    }

    return <ContractFrame contract={contract} viewer={viewer} />;
};

DocumentPreview.propTypes = {
    contract: PropTypes.shape({
        name       : PropTypes.string,
        url        : PropTypes.string,
        previewKind: PropTypes.string
    }).isRequired,
    viewer: PropTypes.shape({
        loading     : PropTypes.bool,
        error       : PropTypes.string,
        status      : PropTypes.string,
        frameReady  : PropTypes.bool,
        frameKey    : PropTypes.number,
        onFrameReady: PropTypes.func,
        retry       : PropTypes.func
    }).isRequired,
    onOpenExternally: PropTypes.func.isRequired
};

export default DocumentPreview;
